import React, { useState } from 'react';
import { riskAnalysisService } from '../../services/riskAnalysisService';
import { createRiskAnalysisRequest } from '../../models/riskAnalysisModels';
import LoadingSpinner from '../UI/LoadingSpinner';

const RiskAnalysisButton = ({ 
  ticker, 
  companyName, 
  onAnalysisComplete, 
  onError,
  className = '',
  variant = 'primary' // 'primary', 'secondary', 'outline'
}) => {
  const [loading, setLoading] = useState(false);
  const [cacheStatus, setCacheStatus] = useState(null);

  const handleAnalyzeRisk = async () => {
    if (!ticker) {
      onError?.('Ticker is required for risk analysis');
      return;
    }

    setLoading(true);
    try {
      const cachedResult = await riskAnalysisService.getCachedRiskAnalysis(ticker);

      if (cachedResult && !cachedResult.cacheStatus.isExpired) {
        setCacheStatus(cachedResult.cacheStatus);
        onAnalysisComplete?.(cachedResult.riskAnalysis, true, cachedResult.cacheStatus);
        return;
      }

      // Perform new analysis
      const analysisRequest = createRiskAnalysisRequest({
        ticker,
        companyName,
        daysBack: 30,
        maxNewsArticles: 50,
        includeIndustryProfile: true,
        enableAIAnalysis: true,
        enableSentimentAnalysis: true,
        saveToDatabase: true,
        updateCompanyProfile: true,
        confidenceThreshold: 0.7,
        minRelevanceScore: 0.1,
        focusRiskCategories: [
          'supply_chain',
          'manufacturing',
          'regulatory',
          'market_competition',
          'technology_disruption',
          'cybersecurity',
          'geopolitical',
          'financial',
          'operational'
        ],
        analysisContext: `Comprehensive risk analysis for ${companyName || ticker} focusing on supply chain vulnerabilities, manufacturing risks, regulatory compliance, and competitive threats.`,
        requestId: `RISK_ANALYSIS_${ticker}_${Date.now()}`,
        requestTimestamp: new Date().toISOString()
      });

      const result = await riskAnalysisService.performRiskAnalysisWithCache(ticker, analysisRequest);
      
      if (result.success) {
        setCacheStatus({ hasCache: true, isExpired: false, ageMinutes: 0 });
        onAnalysisComplete?.(result.data, result.fromCache, result.cacheAge);
      } else {
        onError?.(result.message || 'Failed to perform risk analysis');
      }
    } catch (error) {
      onError?.(error.message || 'An error occurred during risk analysis');
    } finally {
      setLoading(false);
    }
  };

  const getButtonStyles = () => {
    const baseStyles = "inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200";
    
    switch (variant) {
      case 'primary':
        return `${baseStyles} bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500 disabled:bg-blue-300`;
      case 'secondary':
        return `${baseStyles} bg-gray-600 text-white hover:bg-gray-700 focus:ring-gray-500 disabled:bg-gray-300`;
      case 'outline':
        return `${baseStyles} border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400`;
      default:
        return `${baseStyles} bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500 disabled:bg-blue-300`;
    }
  };

  const getButtonText = () => {
    if (loading) {
      return 'Analyzing...';
    }
    
    if (cacheStatus?.hasCache && !cacheStatus?.isExpired) {
      return `Re-analyze Risk (${cacheStatus.ageMinutes}m old)`;
    }
    
    return 'Analyze Risk';
  };

  return (
    <button
      onClick={handleAnalyzeRisk}
      disabled={loading}
      className={`${getButtonStyles()} ${className}`}
    >
      {loading ? (
        <LoadingSpinner size="sm" className="mr-2" />
      ) : (
        <svg 
          className="w-4 h-4 mr-2" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" 
          />
        </svg>
      )}
      {getButtonText()}
    </button>
  );
};

export default RiskAnalysisButton;
